/**
 * Agent 决策日志组件
 * 显示 Agent 的决策历史记录列表
 * 支持展开查看详细信息（LLM Prompt、Response）
 * 支持日期筛选和分页
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import {
  getUserAgentDecisionLogs,
  getDecisionLogDates,
  AgentDecisionLog,
  Agent,
  getNameById,
} from '../../services/agentService';

// 触发类型显示映射
const triggerTypeMap: Record<string, { label: string; color: string }> = {
  agent_init: { label: 'Debut Post', color: 'bg-orange-100 text-orange-700' },
  scheduled_post: { label: 'Scheduled Post', color: 'bg-blue-100 text-blue-700' },
  explore: { label: 'Global Explore', color: 'bg-purple-100 text-purple-700' },
  global_explore: { label: 'Global Explore', color: 'bg-purple-100 text-purple-700' },
  skipped_explore: { label: 'Skipped Explore', color: 'bg-gray-100 text-gray-600' },
  follow_new_post: { label: 'Follow Feed', color: 'bg-green-100 text-green-700' },
  received_comment: { label: 'Received Comment', color: 'bg-yellow-100 text-yellow-700' },
  received_like: { label: 'Received Like', color: 'bg-pink-100 text-pink-700' },
  received_follow: { label: 'Received Follow', color: 'bg-indigo-100 text-indigo-700' },
};

// 决策动作显示映射（扩展版，根据触发类型显示不同描述）
const decisionActionMap: Record<string, { label: string; icon: string }> = {
  post: { label: 'Post', icon: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' },
  comment: { label: 'Comment', icon: 'M21 6h-2v9H6v2c0 1.1.9 2 2 2h9l4 4V8c0-1.1-.9-2-2-2zm-4 6H4c-1.1 0-2 .9-2 2v8l4-4h10c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z' },
  like: { label: 'Like', icon: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' },
  follow: { label: 'Follow', icon: 'M12 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z' },
  ignore: { label: 'Ignore', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z' },
  skip: { label: 'Skip', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z' },
};

// 从决策中获取多动作摘要
const getDecisionSummary = (decision: Record<string, unknown> | undefined): string => {
  if (!decision) return 'Pending';

  const posts = (decision.posts as unknown[])?.length || 0;
  const comments = (decision.comments as unknown[])?.length || 0;
  const postLikes = (decision.post_likes as unknown[])?.length || 0;
  const commentLikes = (decision.comment_likes as unknown[])?.length || 0;
  const follows = (decision.follows as unknown[])?.length || 0;
  const ignore = decision.ignore as boolean;

  if (ignore) return 'Ignored';

  const parts: string[] = [];
  if (posts > 0) parts.push(`${posts} post${posts > 1 ? 's' : ''}`);
  if (comments > 0) parts.push(`${comments} comment${comments > 1 ? 's' : ''}`);
  if (postLikes > 0) parts.push(`${postLikes} like${postLikes > 1 ? 's' : ''}`);
  if (commentLikes > 0) parts.push(`${commentLikes} c-like${commentLikes > 1 ? 's' : ''}`);
  if (follows > 0) parts.push(`${follows} follow${follows > 1 ? 's' : ''}`);

  return parts.length > 0 ? parts.join(' / ') : 'No Action';
};

// 根据触发类型和动作组合，显示更友好的描述
const getActionLabelWithTrigger = (triggerType: string, action: string | null): string => {
  if (!action) return 'Pending';

  // 特殊组合显示
  if (triggerType === 'agent_init') {
    if (action === 'post') return 'Debut Post';
    if (action === 'follow') return 'Follow Account';
    if (action === 'ignore') return 'No Action';
  }

  if (triggerType === 'scheduled_post') {
    if (action === 'post') return 'Publish Post';
    if (action === 'ignore') return 'Skip Posting';
  }

  if (triggerType === 'global_explore') {
    if (action === 'comment') return 'Interactive Comment';
    if (action === 'like') return 'Explore Like';
    if (action === 'follow') return 'Discover Follow';
    if (action === 'ignore') return 'Not Interested';
  }

  if (triggerType === 'received_comment') {
    if (action === 'comment') return 'Reply Comment';
    if (action === 'like') return 'Like to Thank';
    if (action === 'follow') return 'Follow Commenter';
    if (action === 'ignore') return 'No Reply';
  }

  if (triggerType === 'received_like') {
    if (action === 'follow') return 'Follow Liker';
    if (action === 'like') return 'Interactive Like';
    if (action === 'ignore') return 'No Response';
  }

  if (triggerType === 'received_follow') {
    if (action === 'follow') return 'Follow Back';
    if (action === 'ignore') return 'No Follow Back';
  }

  if (triggerType === 'follow_new_post') {
    if (action === 'comment') return 'Comment on Follow';
    if (action === 'like') return 'Like on Follow';
    if (action === 'unfollow') return 'Unfollow';
    if (action === 'ignore') return 'No Interaction';
  }

  // 默认显示
  return decisionActionMap[action]?.label || action;
};

// 格式化时间显示
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// 安全获取嵌套对象属性
const getNestedValue = (obj: unknown, path: string[]): unknown => {
  let current: unknown = obj;
  for (const key of path) {
    if (current === null || current === undefined) return undefined;
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return current;
};

// 触发上下文详情组件
interface TriggerContextDetailProps {
  triggerType: string;
  context: unknown;
  commenterName?: string;
  likerName?: string;
  followerName?: string;
}

const TriggerContextDetail: React.FC<TriggerContextDetailProps> = ({
  triggerType,
  context,
  commenterName,
  likerName,
  followerName,
}) => {
  const ctx = context as Record<string, unknown> | null;
  if (!ctx) return null;

  const rawTriggerContent = ctx.triggerContent || ctx;
  const triggerContent = rawTriggerContent as Record<string, unknown>;
  const decision = ctx.decision as Record<string, unknown> | undefined;

  // 收到评论触发
  if (triggerType === 'received_comment') {
    const name = commenterName
      || String(getNestedValue(triggerContent, ['commenter_name']) || '')
      || String(getNestedValue(decision, ['commenter_name']) || '');
    const commentContent = String(
      getNestedValue(triggerContent, ['comment_content']) ||
      getNestedValue(triggerContent, ['comment', 'content']) || ''
    );
    const commenterType = String(getNestedValue(triggerContent, ['commenter_type']) || '');
    const commenterId = String(getNestedValue(triggerContent, ['commenter_id']) || '');
    const postId = String(getNestedValue(triggerContent, ['post_id']) ||
      getNestedValue(triggerContent, ['post', 'id']) || '');

    return (
      <div className="bg-[#FFF8E0] rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-yellow-700 font-medium">Commenter:</span>
          <span className="text-sm text-[#333333]">{name || 'User'}</span>
          {commenterType && (
            <span className="text-xs text-[#999999] bg-[#F5F5F7] px-1.5 py-0.5 rounded">
              {commenterType === 'human' ? 'Human' : 'Agent'}
            </span>
          )}
          {!name && commenterId && (
            <span className="text-xs text-[#999999]">
              (ID: {commenterId.substring(0, 8)}...)
            </span>
          )}
        </div>
        {commentContent && (
          <div className="text-sm text-[#666666] bg-white rounded p-2 border border-yellow-200">
            "{commentContent}"
          </div>
        )}
        {postId && (
          <div className="text-xs text-[#999999]">
            Post ID: {postId.substring(0, 8)}...
          </div>
        )}
      </div>
    );
  }

  // 收到点赞触发
  if (triggerType === 'received_like') {
    const name = likerName
      || String(getNestedValue(triggerContent, ['liker_name']) || '')
      || String(getNestedValue(decision, ['liker_name']) || '');
    const likerType = String(getNestedValue(triggerContent, ['liker_type']) || '');
    const likerId = String(getNestedValue(triggerContent, ['liker_id']) || '');
    const postId = String(getNestedValue(triggerContent, ['post_id']) ||
      getNestedValue(triggerContent, ['post', 'id']) || '');

    return (
      <div className="bg-[#FFF0F5] rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-pink-700 font-medium">Liker:</span>
          <span className="text-sm text-[#333333]">{name || 'User'}</span>
          {likerType && (
            <span className="text-xs text-[#999999] bg-[#F5F5F7] px-1.5 py-0.5 rounded">
              {likerType === 'human' ? 'Human' : 'Agent'}
            </span>
          )}
          {!name && likerId && (
            <span className="text-xs text-[#999999]">
              (ID: {likerId.substring(0, 8)}...)
            </span>
          )}
        </div>
        {postId && (
          <div className="text-xs text-[#999999]">
            Post ID: {postId.substring(0, 8)}...
          </div>
        )}
      </div>
    );
  }

  // 收到关注触发
  if (triggerType === 'received_follow') {
    const name = followerName
      || String(getNestedValue(triggerContent, ['follower_name']) || '')
      || String(getNestedValue(decision, ['follower_name']) || '');
    const followerType = String(getNestedValue(triggerContent, ['follower_type']) || '');
    const followerId = String(getNestedValue(triggerContent, ['follower_id']) || '');

    return (
      <div className="bg-[#E8E4F0] rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-indigo-700 font-medium">Follower:</span>
          <span className="text-sm text-[#333333]">{name || 'User'}</span>
          {followerType && (
            <span className="text-xs text-[#999999] bg-[#F5F5F7] px-1.5 py-0.5 rounded">
              {followerType === 'human' ? 'Human' : 'Agent'}
            </span>
          )}
          {!name && followerId && (
            <span className="text-xs text-[#999999]">
              (ID: {followerId.substring(0, 8)}...)
            </span>
          )}
        </div>
        <div className="text-xs text-[#666666]">
          Followed you
        </div>
      </div>
    );
  }

  // 关注对象新帖子
  if (triggerType === 'follow_new_post') {
    const authorName = String(getNestedValue(triggerContent, ['author_name']) || '');
    const postContent = String(
      getNestedValue(triggerContent, ['post', 'content']) ||
      getNestedValue(triggerContent, ['post_content']) || ''
    );

    return (
      <div className="bg-[#E8F5E9] rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-green-700 font-medium">Author:</span>
          <span className="text-sm text-[#333333]">{authorName || 'User'}</span>
        </div>
        {postContent && (
          <div className="text-sm text-[#666666] bg-white rounded p-2 border border-green-200">
            "{postContent.substring(0, 100)}{postContent.length > 100 ? '...' : ''}"
          </div>
        )}
      </div>
    );
  }

  // 全局探索
  if (triggerType === 'explore' || triggerType === 'global_explore') {
    const posts = getNestedValue(triggerContent, ['posts']) as Array<Record<string, unknown>> | undefined;

    if (posts && posts.length > 0) {
      return (
        <div className="bg-[#F3E5F5] rounded-lg p-3 space-y-2">
          <div className="text-xs text-purple-700 font-medium mb-2">Explored Content:</div>
          <div className="space-y-1">
            {posts.slice(0, 3).map((post, idx) => {
              const content = String(post.content || 'No content');
              return (
                <div key={idx} className="text-sm text-[#666666] bg-white rounded p-2 border border-purple-100">
                  {content.substring(0, 50)}...
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }

  // 跳过探索
  if (triggerType === 'skipped_explore') {
    const reason = String(getNestedValue(triggerContent, ['reason']) || 'No available posts');
    const interactedCount = Number(getNestedValue(triggerContent, ['interacted_count']) || 0);

    return (
      <div className="bg-[#F0F0F2] rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600 font-medium">Skip Reason:</span>
          <span className="text-sm text-[#333333]">{reason === 'no_available_posts' ? 'No new posts available' : reason}</span>
        </div>
        {interactedCount > 0 && (
          <div className="text-xs text-[#666666]">
            Interacted posts: {interactedCount} (already liked or commented)
          </div>
        )}
        <div className="text-xs text-[#999999] bg-white rounded p-2 border border-gray-200 mt-2">
          This exploration was skipped, will switch to active posting next time
        </div>
      </div>
    );
  }

  // 其他类型或无具体内容时，显示通用格式
  return (
    <div className="bg-[#F0F0F2] rounded-lg p-3 text-xs text-[#666666]">
      <pre className="whitespace-pre-wrap overflow-auto max-h-[150px]">
        {JSON.stringify(ctx, null, 2)}
      </pre>
    </div>
  );
};

// 单条决策日志卡片
interface DecisionLogItemProps {
  log: AgentDecisionLog;
  agentName?: string;
  isExpanded: boolean;
  onToggle: () => void;
  userNameCache: React.MutableRefObject<Record<string, string>>;
}

const DecisionLogItem: React.FC<DecisionLogItemProps> = ({
  log,
  agentName,
  isExpanded,
  onToggle,
  userNameCache,
}) => {
  const [commenterName, setCommenterName] = useState<string | null>(null);
  const [likerName, setLikerName] = useState<string | null>(null);
  const [followerName, setFollowerName] = useState<string | null>(null);
  const [isLoadingNames, setIsLoadingNames] = useState(false);

  const triggerInfo = triggerTypeMap[log.trigger_type] || {
    label: log.trigger_type,
    color: 'bg-gray-100 text-gray-700',
  };

  // 获取 X 平台同步状态详情
  const getXSyncStatus = (): { status: 'success' | 'failed' | 'none'; tweetId?: string; error?: string; syncedAt?: string } => {
    // 只检查发帖类型（agent_init 和 scheduled_post）
    if (log.trigger_type !== 'agent_init' && log.trigger_type !== 'scheduled_post') {
      return { status: 'none' };
    }

    // 优先检查决策日志表的 sync_status 字段（由 sync-post Edge Function 直接更新）
    // 注意：sync_status 可能是 JSON 字符串或对象
    if (log.sync_status) {
      let parsedSyncStatus: Record<string, { success: boolean; error?: string; platform_post_id?: string; synced_at?: string }> | null = null;

      if (typeof log.sync_status === 'string') {
        try {
          parsedSyncStatus = JSON.parse(log.sync_status);
        } catch {
          // 解析失败，忽略
        }
      } else {
        parsedSyncStatus = log.sync_status as Record<string, { success: boolean; error?: string; platform_post_id?: string; synced_at?: string }>;
      }

      if (parsedSyncStatus) {
        const xStatus = parsedSyncStatus.x;
        if (xStatus) {
          if (xStatus.success === true) {
            return {
              status: 'success',
              tweetId: xStatus.platform_post_id,
              syncedAt: xStatus.synced_at,
            };
          } else {
            return {
              status: 'failed',
              error: xStatus.error,
              syncedAt: xStatus.synced_at,
            };
          }
        }
      }
    }

    const context = log.trigger_context as Record<string, unknown> | null;
    if (!context) return { status: 'none' };

    // 检查 executionDetails.posts 中的同步状态
    const executionDetails = context.executionDetails as Record<string, unknown> | undefined;
    if (executionDetails) {
      const posts = executionDetails.posts as Array<Record<string, unknown>> | undefined;
      if (posts && posts.length > 0) {
        for (const post of posts) {
          const syncStatus = post.sync_status as Record<string, unknown> | undefined;
          if (syncStatus) {
            // 直接检查 details.x 是否存在（无论 platforms 是否包含 x）
            const details = syncStatus.details as Record<string, unknown> | undefined;
            const xDetails = details?.x as Record<string, unknown> | undefined;
            if (xDetails) {
              if (xDetails.success === true) {
                return {
                  status: 'success',
                  tweetId: xDetails.platform_post_id as string | undefined || xDetails.tweetId as string | undefined,
                  syncedAt: xDetails.synced_at as string | undefined,
                };
              } else {
                return {
                  status: 'failed',
                  error: xDetails.error as string | undefined,
                  syncedAt: xDetails.synced_at as string | undefined,
                };
              }
            }
          }
        }
      }
    }

    // 检查 trigger_context 顶层 sync_status 字段
    const topLevelSyncStatus = context.sync_status as Record<string, unknown> | undefined;
    if (topLevelSyncStatus) {
      const xStatus = topLevelSyncStatus.x as Record<string, unknown> | undefined;
      if (xStatus) {
        if (xStatus.success === true) {
          return {
            status: 'success',
            tweetId: xStatus.platform_post_id as string | undefined,
            syncedAt: xStatus.synced_at as string | undefined,
          };
        } else {
          return {
            status: 'failed',
            error: xStatus.error as string | undefined,
            syncedAt: xStatus.synced_at as string | undefined,
          };
        }
      }
    }

    return { status: 'none' };
  };

  const xSyncStatus = getXSyncStatus();

  // 获取执行状态显示
const getExecutionStatus = (log: AgentDecisionLog): { label: string; colorClass: string } => {
  if (log.executed) {
    return { label: 'Executed', colorClass: 'bg-green-100 text-green-700' };
  }
  // 如果有执行错误，说明已经执行了但失败
  if (log.execution_error) {
    return { label: 'Failed', colorClass: 'bg-red-100 text-red-700' };
  }
  // 否则是待执行状态
  return { label: 'Pending', colorClass: 'bg-gray-100 text-gray-600' };
};

const executionStatus = getExecutionStatus(log);

  const actionInfo = decisionActionMap[log.decision_action || ''] || {
    label: getActionLabelWithTrigger(log.trigger_type, log.decision_action),
    icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z',
  };

  // 获取触发内容摘要
  const getTriggerSummary = () => {
    const context = log.trigger_context as Record<string, unknown> | null;
    if (!context) return null;

    const triggerContent = (context.triggerContent || context) as Record<string, unknown>;

    if (log.trigger_type === 'received_comment') {
      const commenterId = String(getNestedValue(triggerContent, ['commenter_id']) || '');
      const commenterType = String(getNestedValue(triggerContent, ['commenter_type']) || '');
      const commentContent = String(
        getNestedValue(triggerContent, ['comment_content']) ||
        getNestedValue(triggerContent, ['comment', 'content']) || ''
      );

      const cachedName = commenterId ? userNameCache.current[commenterId] : null;
      const displayName = cachedName || commenterName;

      if (displayName || commenterId) {
        return {
          type: 'comment' as const,
          summary: `${displayName || 'User'} commented: "${commentContent.substring(0, 30)}${commentContent.length > 30 ? '...' : ''}"`,
          commenterId,
          commenterType,
        };
      }
    }

    if (log.trigger_type === 'received_like') {
      const likerId = String(getNestedValue(triggerContent, ['liker_id']) || '');
      const likerType = String(getNestedValue(triggerContent, ['liker_type']) || '');

      const cachedName = likerId ? userNameCache.current[likerId] : null;
      const displayName = cachedName || likerName;

      if (displayName || likerId) {
        return {
          type: 'like' as const,
          summary: `${displayName || 'User'} liked your post`,
          likerId,
          likerType,
        };
      }
    }

    if (log.trigger_type === 'received_follow') {
      const followerId = String(getNestedValue(triggerContent, ['follower_id']) || '');
      const followerType = String(getNestedValue(triggerContent, ['follower_type']) || '');

      const cachedName = followerId ? userNameCache.current[followerId] : null;
      const displayName = cachedName || followerName;

      if (displayName || followerId) {
        return {
          type: 'follow' as const,
          summary: `${displayName || 'User'} followed you`,
          followerId,
          followerType,
        };
      }
    }

    if (log.trigger_type === 'follow_new_post') {
      const authorName = String(getNestedValue(triggerContent, ['author_name']) || '');
      if (authorName) {
        return {
          type: 'new_post' as const,
          summary: `${authorName} published new content`,
        };
      }
    }

    return null;
  };

  const triggerSummary = getTriggerSummary();

  // 展开时查询用户名称
  useEffect(() => {
    if (!isExpanded || isLoadingNames) return;

    const fetchNames = async () => {
      setIsLoadingNames(true);

      const context = log.trigger_context as Record<string, unknown> | null;
      if (!context) {
        setIsLoadingNames(false);
        return;
      }

      const triggerContent = (context.triggerContent || context) as Record<string, unknown>;

      // 查询评论者名称
      if (log.trigger_type === 'received_comment') {
        const commenterId = String(getNestedValue(triggerContent, ['commenter_id']) || '');
        const commenterType = (getNestedValue(triggerContent, ['commenter_type']) as 'human' | 'agent') || 'human';

        if (commenterId && !userNameCache.current[commenterId]) {
          const { name } = await getNameById(commenterId, commenterType);
          if (name) {
            userNameCache.current[commenterId] = name;
            setCommenterName(name);
          }
        } else if (userNameCache.current[commenterId]) {
          setCommenterName(userNameCache.current[commenterId]);
        }
      }

      // 查询点赞者名称
      if (log.trigger_type === 'received_like') {
        const likerId = String(getNestedValue(triggerContent, ['liker_id']) || '');
        const likerType = (getNestedValue(triggerContent, ['liker_type']) as 'human' | 'agent') || 'human';

        if (likerId && !userNameCache.current[likerId]) {
          const { name } = await getNameById(likerId, likerType);
          if (name) {
            userNameCache.current[likerId] = name;
            setLikerName(name);
          }
        } else if (userNameCache.current[likerId]) {
          setLikerName(userNameCache.current[likerId]);
        }
      }

      // 查询关注者名称
      if (log.trigger_type === 'received_follow') {
        const followerId = String(getNestedValue(triggerContent, ['follower_id']) || '');
        const followerType = (getNestedValue(triggerContent, ['follower_type']) as 'human' | 'agent') || 'human';

        if (followerId && !userNameCache.current[followerId]) {
          const { name } = await getNameById(followerId, followerType);
          if (name) {
            userNameCache.current[followerId] = name;
            setFollowerName(name);
          }
        } else if (userNameCache.current[followerId]) {
          setFollowerName(userNameCache.current[followerId]);
        }
      }

      setIsLoadingNames(false);
    };

    fetchNames();
  }, [isExpanded, log.trigger_type, log.trigger_context, userNameCache, isLoadingNames]);

  return (
    <div className="decision-log-item bg-white rounded-xl border border-[#EBEBEB] overflow-hidden transition-all">
      {/* 主内容区 */}
      <div
        className="p-4 md:p-5 cursor-pointer hover:bg-[#F8F8F9] transition-colors"
        onClick={onToggle}
      >
        {/* 头部：触发类型 + 时间 */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${triggerInfo.color}`}>
              {triggerInfo.label}
            </span>
            {agentName && (
              <span className="text-xs text-[#999999] bg-[#F5F5F7] px-2 py-1 rounded-full">
                {agentName}
              </span>
            )}
            {xSyncStatus.status === 'success' && (
              <span className="text-xs text-[#1DA1F2] bg-[#E8F4FD] px-2 py-1 rounded-full flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.427 8.588 8.752 11.162H14.91l-5.592-7.292-6.36 7.292H1.65l7.79-8.883L1.254 2.25H8.91l5.095 6.694 5.25-6.694zm-1.272 17.708h1.828L7.29 4.91H5.42l11.552 15.048z"/>
                </svg>
                Synced
              </span>
            )}
            {xSyncStatus.status === 'failed' && (
              <span className="text-xs text-[#FF3B30] bg-[#FFE5E5] px-2 py-1 rounded-full flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                X Failed
              </span>
            )}
          </div>
          <span className="text-xs text-[#999999]">{formatTime(log.created_at)}</span>
        </div>

        {/* 触发内容摘要 */}
        {triggerSummary && (
          <div className="mb-2 text-sm text-[#666666] flex items-center gap-1.5">
            {triggerSummary.type === 'comment' && (
              <svg className="w-4 h-4 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 6h-2v9H6v2c0 1.1.9 2 2 2h9l4 4V8c0-1.1-.9-2-2-2zm-4 6H4c-1.1 0-2 .9-2 2v8l4-4h10c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z" />
              </svg>
            )}
            {triggerSummary.type === 'like' && (
              <svg className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            )}
            {triggerSummary.type === 'follow' && (
              <svg className="w-4 h-4 text-indigo-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z" />
              </svg>
            )}
            {triggerSummary.type === 'new_post' && (
              <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
            )}
            <span className="text-[#555555]">{triggerSummary.summary}</span>
          </div>
        )}

        {/* 决策动作 + 能量消耗 */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[#333333]" viewBox="0 0 24 24" fill="currentColor">
              <path d={actionInfo.icon} />
            </svg>
            <span className="text-sm font-medium text-[#333333]">
              {getActionLabelWithTrigger(log.trigger_type, log.decision_action)}
            </span>
            {/* 显示多动作摘要 */}
            {(() => {
              const context = log.trigger_context as Record<string, unknown> | null;
              const decision = context?.decision as Record<string, unknown> | undefined;
              const summary = getDecisionSummary(decision);
              if (summary !== 'Pending' && summary !== 'No Action') {
                return (
                  <span className="text-xs text-[#666666] bg-[#F5F5F7] px-1.5 py-0.5 rounded ml-1">
                    {summary}
                  </span>
                );
              }
              return null;
            })()}
          </div>
          <div className="flex items-center gap-3">
            {log.energy_cost > 0 && (
              <span className="text-xs text-[#FF9F0A] bg-[#FFF8E0] px-1.5 py-0.5 rounded">
                -{log.energy_cost} energy
              </span>
            )}
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${executionStatus.colorClass}`}
            >
              {executionStatus.label}
            </span>
          </div>
        </div>

        {/* 决策内容预览 */}
        {log.decision_content && (
          <div className="text-sm text-[#666666] leading-relaxed text-truncate-2">
            {log.decision_content}
          </div>
        )}

        {/* 展开箭头 */}
        <div className="flex justify-center mt-2">
          <svg
            className={`w-5 h-5 text-[#999999] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
          </svg>
        </div>
      </div>

      {/* 详细信息区 */}
      {isExpanded && (
        <div className="px-4 md:px-5 pb-4 md:pb-5 border-t border-[#EBEBEB] bg-[#FAFAFA]">
          {/* 触发上下文详情 */}
          {log.trigger_context && (
            <div className="mb-4 pt-4">
              <div className="text-xs text-[#999999] mb-2 font-medium">Trigger Details</div>
              <TriggerContextDetail
                triggerType={log.trigger_type}
                context={log.trigger_context}
                commenterName={commenterName || undefined}
                likerName={likerName || undefined}
                followerName={followerName || undefined}
              />
            </div>
          )}

          {/* X 平台同步详情 */}
          {xSyncStatus.status !== 'none' && (
            <div className="mb-4 pt-4">
              <div className="text-xs text-[#999999] mb-2 font-medium flex items-center gap-2">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.427 8.588 8.752 11.162H14.91l-5.592-7.292-6.36 7.292H1.65l7.79-8.883L1.254 2.25H8.91l5.095 6.694 5.25-6.694zm-1.272 17.708h1.828L7.29 4.91H5.42l11.552 15.048z"/>
                </svg>
                X Platform Sync
              </div>
              <div className={`rounded-lg p-3 ${xSyncStatus.status === 'success' ? 'bg-[#E8F4FD]' : 'bg-[#FFE5E5]'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium ${xSyncStatus.status === 'success' ? 'text-[#1DA1F2]' : 'text-[#FF3B30]'}`}>
                    {xSyncStatus.status === 'success' ? 'Successfully synced to X' : 'Sync failed'}
                  </span>
                </div>
                {xSyncStatus.status === 'success' && xSyncStatus.tweetId && (
                  <div className="text-xs text-[#666666] mb-1">
                    Tweet ID: {xSyncStatus.tweetId}
                  </div>
                )}
                {xSyncStatus.status === 'success' && xSyncStatus.tweetId && (
                  <a
                    href={`https://x.com/i/status/${xSyncStatus.tweetId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#1DA1F2] hover:underline"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" transform="rotate(180 12 12)"/>
                    </svg>
                    View on X
                  </a>
                )}
                {xSyncStatus.status === 'failed' && xSyncStatus.error && (
                  <div className="text-xs text-[#FF3B30] bg-white rounded p-2 border border-red-200">
                    {xSyncStatus.error}
                  </div>
                )}
                {xSyncStatus.syncedAt && (
                  <div className="text-xs text-[#999999] mt-2">
                    Synced at: {new Date(xSyncStatus.syncedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 执行置信度 */}
          {log.decision_confidence && (
            <div className="mb-4 pt-4">
              <div className="text-xs text-[#999999] mb-1">Decision Confidence</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-[#E0E0E0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#34C759] to-[#30D158] rounded-full"
                    style={{ width: `${(log.decision_confidence || 0) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-[#333333]">
                  {((log.decision_confidence || 0) * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          )}

          {/* 执行详情 - 多动作展示 */}
          {(() => {
            const context = log.trigger_context as Record<string, unknown> | null;
            const executionDetails = context?.executionDetails as Record<string, unknown> | undefined;
            const decision = context?.decision as Record<string, unknown> | undefined;

            if (!executionDetails && !decision) return null;

            const follows = (executionDetails?.follows as unknown[]) || [];
            const postLikes = (executionDetails?.post_likes as unknown[]) || [];
            const commentLikes = (executionDetails?.comment_likes as unknown[]) || [];
            const comments = (executionDetails?.comments as unknown[]) || [];
            const decisionComments = (decision?.comments as unknown[]) || [];
            const decisionPostLikes = (decision?.post_likes as unknown[]) || [];
            const decisionFollows = (decision?.follows as unknown[]) || [];

            if (follows.length === 0 && postLikes.length === 0 && commentLikes.length === 0 &&
                comments.length === 0 && decisionComments.length === 0 &&
                decisionPostLikes.length === 0 && decisionFollows.length === 0) {
              return null;
            }

            return (
              <div className="mb-4 pt-4">
                <div className="text-xs text-[#999999] mb-2 font-medium">Execution Details</div>
                <div className="flex flex-wrap gap-2">
                  {(decisionFollows.length > 0 || follows.length > 0) && (
                    <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full">
                      Follow {follows.length > 0 ? follows.length : decisionFollows.length} user{((follows.length > 0 ? follows.length : decisionFollows.length) > 1) ? 's' : ''}
                    </span>
                  )}
                  {(decisionPostLikes.length > 0 || postLikes.length > 0) && (
                    <span className="text-xs bg-pink-50 text-pink-700 px-2 py-1 rounded-full">
                      Like {postLikes.length > 0 ? postLikes.length : decisionPostLikes.length} post{((postLikes.length > 0 ? postLikes.length : decisionPostLikes.length) > 1) ? 's' : ''}
                    </span>
                  )}
                  {commentLikes.length > 0 && (
                    <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded-full">
                      Like {commentLikes.length} comment{commentLikes.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {(decisionComments.length > 0 || comments.length > 0) && (
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                      Comment {comments.length > 0 ? comments.length : decisionComments.length} post{((comments.length > 0 ? comments.length : decisionComments.length) > 1) ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* LLM Prompt */}
          {log.llm_prompt && (
            <div className="mb-4">
              <div className="text-xs text-[#999999] mb-1 font-medium">LLM Prompt</div>
              <div className="bg-[#F0F0F2] rounded-lg p-3 text-xs text-[#666666] leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                {log.llm_prompt}
              </div>
            </div>
          )}

          {/* LLM Response */}
          {log.llm_response && (
            <div className="mb-4">
              <div className="text-xs text-[#999999] mb-1 font-medium">LLM Response</div>
              <div className="bg-[#F0F0F2] rounded-lg p-3 text-xs text-[#666666] leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                {log.llm_response}
              </div>
            </div>
          )}

          {/* 执行错误 */}
          {log.execution_error && (
            <div className="mb-4">
              <div className="text-xs text-[#FF3B30] mb-1 font-medium">Execution Error</div>
              <div className="bg-[#FFF0F0] rounded-lg p-3 text-xs text-[#FF3B30] leading-relaxed">
                {log.execution_error}
              </div>
            </div>
          )}

          {/* 执行耗时 */}
          {log.duration_ms && (
            <div className="text-xs text-[#999999] pt-2">
              Execution time: {log.duration_ms}ms
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 主组件
interface AgentDecisionLogsProps {
  agents?: Agent[];
}

const pageSize = 20;

const AgentDecisionLogs: React.FC<AgentDecisionLogsProps> = ({ agents }) => {
  const { user, isLoggedIn } = useAuthStore();

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedDate, setSelectedDate] = useState<string>(''); // 空表示当天
  const userNameCache = React.useRef<Record<string, string>>({});

  const agentNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    if (agents) {
      agents.forEach(agent => {
        map[agent.id] = agent.name;
      });
    }
    return map;
  }, [agents]);

  // 加载可用日期列表 - 使用 React Query
  const { data: availableDates = [] } = useQuery({
    queryKey: ['decisionLogDates', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await getDecisionLogDates(user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // 10分钟内不重新拉取
    refetchOnMount: 'always', // 每次打开都刷新，确保日期列表是最新的
  });

  // 加载决策日志 - 使用 React Query useInfiniteQuery
  const {
    data: logsData,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['decisionLogs', user?.id, selectedDate],
    queryFn: async ({ pageParam }) => {
      if (!user) return { data: [], total: 0, nextOffset: null };
      const { data, total, error } = await getUserAgentDecisionLogs(user.id, {
        date: selectedDate || undefined,
        limit: pageSize,
        offset: pageParam as number,
      });
      if (error) throw error;
      return {
        data: data || [],
        total,
        nextOffset: (pageParam as number) + pageSize < total ? (pageParam as number) + pageSize : null,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5分钟内不重新拉取
    refetchOnMount: 'always', // 每次打开都刷新，确保看到最新决策
  });

  // 扁平化日志数据
  const logs = logsData?.pages.flatMap(page => page.data) || [];
  const totalCount = logsData?.pages[0]?.total || 0;

  // 格式化日期显示
  const formatDateDisplay = (dateStr: string): string => {
    if (!dateStr) return 'Today';

    const today = new Date().toISOString().split('T')[0];
    if (dateStr === today) return 'Today';

    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    if (dateStr === yesterday) return 'Yesterday';

    return dateStr;
  };

  const handleToggleExpand = (logId: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setExpandedIds(new Set()); // 切换日期时清空展开状态
  };

  return (
    <div className="decision-logs-section">
      {/* 标题 + 日期筛选 */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="section-title text-lg font-semibold text-[#333333]">
          Decision Logs
        </h2>

        {/* 快捷日期选择 */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* 快捷按钮 */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleDateChange('')}
              className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                selectedDate === ''
                  ? 'bg-[#FF2442] text-white'
                  : 'bg-[#F5F5F7] text-[#666666] hover:bg-[#E8E8EA]'
              }`}
            >
              Today
            </button>
            {availableDates.length > 0 && availableDates[0] === new Date().toISOString().split('T')[0] && availableDates[1] && (
              <button
                onClick={() => handleDateChange(availableDates[1])}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  selectedDate === availableDates[1]
                    ? 'bg-[#FF2442] text-white'
                    : 'bg-[#F5F5F7] text-[#666666] hover:bg-[#E8E8EA]'
                }`}
              >
                Yesterday
              </button>
            )}
          </div>

          {/* 日期下拉选择 */}
          {availableDates.length > 0 && (
            <select
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="text-sm border border-[#E0E0E0] rounded-lg px-3 py-1.5 bg-white text-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FF2442] focus:border-transparent"
            >
              <option value="">Today</option>
              {availableDates.map(date => (
                <option key={date} value={date}>{formatDateDisplay(date)}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* 数据统计 */}
      {!isLoading && isLoggedIn && logs.length > 0 && (
        <div className="text-xs text-[#999999] mb-3">
          {formatDateDisplay(selectedDate)}: {totalCount} record{totalCount > 1 ? 's' : ''}
          {totalCount > pageSize && ` · ${logs.length} loaded`}
        </div>
      )}

      {/* 首次加载骨架屏 */}
      {isLoading && logs.length === 0 && (
        <div className="space-y-3 md:space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="decision-log-skeleton">
              <div className="flex items-center justify-between mb-3">
                <div className="skeleton-tag"></div>
                <div className="skeleton-text-short"></div>
              </div>
              <div className="skeleton-text mb-2"></div>
              <div className="skeleton-text-short"></div>
            </div>
          ))}
        </div>
      )}

      {/* 错误提示 */}
      {isError && !isLoading && logs.length === 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
          Failed to load decision logs, please try again later
        </div>
      )}

      {/* 未登录提示 */}
      {!isLoggedIn && !isLoading && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="text-[#999999] text-sm">Please log in to view decision logs</div>
        </div>
      )}

      {/* 空状态 */}
      {isLoggedIn && !isLoading && !isError && logs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <svg className="w-12 h-12 text-[#D0D0D0] mb-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
          </svg>
          <div className="text-[#999999] text-sm">
            {selectedDate ? `No decision logs for ${formatDateDisplay(selectedDate)}` : 'No decision logs for today'}
          </div>
          {selectedDate === '' && availableDates.length > 0 && (
            <button
              onClick={() => handleDateChange(availableDates[0] === new Date().toISOString().split('T')[0] ? availableDates[1] : availableDates[0])}
              className="mt-2 text-sm text-[#FF2442] hover:underline"
            >
              View History
            </button>
          )}
        </div>
      )}

      {/* 日志列表 - React Query 缓存数据立即显示 */}
      {isLoggedIn && logs.length > 0 && (
        <div className="decision-logs-list space-y-3 md:space-y-4">
          {logs.map(log => (
            <DecisionLogItem
              key={log.id}
              log={log}
              agentName={agentNameMap[log.agent_id]}
              isExpanded={expandedIds.has(log.id)}
              onToggle={() => handleToggleExpand(log.id)}
              userNameCache={userNameCache}
            />
          ))}
        </div>
      )}

      {/* 加载更多 */}
      {hasNextPage && !isFetchingNextPage && (
        <div className="text-center mt-4">
          <button
            onClick={() => fetchNextPage()}
            className="text-sm text-[#FF2442] hover:underline px-4 py-2"
          >
            Load More ({logs.length}/{totalCount})
          </button>
        </div>
      )}

      {/* 加载更多中 */}
      {isFetchingNextPage && (
        <div className="text-center mt-4">
          <div className="animate-spin w-5 h-5 border-2 border-[#FF2442] border-t-transparent rounded-full inline-block" />
        </div>
      )}

      {/* 日期切换提示 */}
      {selectedDate && logs.length > 0 && (
        <div className="text-center text-xs text-[#999999] mt-4">
          <button
            onClick={() => handleDateChange('')}
            className="text-[#FF2442] hover:underline"
          >
            Back to Today
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentDecisionLogs;